/**
 * useDebateStream - Hook for consuming Ad-Hoc Debate SSE events.
 *
 * Mounted at DashboardLayout level so the SSE connection survives
 * page navigation. Handles real-time streaming updates:
 * - debate_init: Setup complete, debate ID assigned
 * - topic_classified: Financial vs general
 * - data_gathering_start/complete: Research data fetching
 * - agent_assigned: Debater role assignment
 * - debate_start: Debate beginning
 * - debate_agent_argument: Individual agent arguments
 * - mediator_check: Moderator assessment per round
 * - debate_round_complete: Round finished
 * - debate_complete/done: Final result
 */

import { useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

export interface DebateAgent {
  id: string;
  role: string;
  icon: string;
}

export interface DebateArgument {
  agentId: string;
  agentRole: string;
  agentIcon: string;
  roundNumber: number;
  argument: string;
  isInitial: boolean;
}

export interface MediatorCheck {
  roundNumber: number;
  agreementScore: number;
  resolved: string[];
  unresolved: string[];
  facilitationNote?: string;
}

export interface DebateConsensus {
  synthesis: string;
  score: number;
  rawScore: number;
  resolved: string[];
  unresolved: string[];
  roundsCompleted: number;
  initialDivergence: number;
  convergenceTrajectory: number[];
}

export interface DebateStreamState {
  status: 'idle' | 'initializing' | 'gathering-data' | 'debating' | 'synthesizing' | 'done' | 'error';
  debateId: string;
  question: string;
  depth: string;
  mode: string;
  topicType: string;
  tickers: string[];
  agents: DebateAgent[];
  arguments: DebateArgument[];
  mediatorChecks: MediatorCheck[];
  consensus: DebateConsensus | null;
  dataSources: string[];
  currentRound: number;
  maxRounds: number;
  estimatedSeconds: number;
  error: string | null;
  totalTimeSeconds: number;
}

export interface DebateStreamOptions {
  onComplete?: (result: DebateStreamState) => void;
  onError?: (error: string) => void;
}

export interface DebateStartParams {
  question: string;
  depth: 'fast' | 'deep';
  mode: 'adversarial' | 'multi-perspective';
  context?: {
    page_type: string;
    tickers?: string[];
    selected_ticker?: string;
    portfolio_summary?: Record<string, unknown>;
    research_context?: string;
  };
  reference_debate_id?: string;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: DebateStreamState = {
  status: 'idle',
  debateId: '',
  question: '',
  depth: 'fast',
  mode: 'multi-perspective',
  topicType: '',
  tickers: [],
  agents: [],
  arguments: [],
  mediatorChecks: [],
  consensus: null,
  dataSources: [],
  currentRound: 0,
  maxRounds: 0,
  estimatedSeconds: 0,
  error: null,
  totalTimeSeconds: 0,
};

// ============================================================================
// Hook
// ============================================================================

export function useDebateStream(options?: DebateStreamOptions) {
  const [state, setState] = useState<DebateStreamState>(initialState);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const onCompleteRef = useRef(options?.onComplete);
  const onErrorRef = useRef(options?.onError);
  onCompleteRef.current = options?.onComplete;
  onErrorRef.current = options?.onError;

  const disconnect = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const reset = useCallback(() => {
    disconnect();
    setState(initialState);
  }, [disconnect]);

  const startDebate = useCallback(async (params: DebateStartParams) => {
    // Reset state
    setState({
      ...initialState,
      status: 'initializing',
      question: params.question,
      depth: params.depth,
      mode: params.mode,
    });
    setIsConnecting(true);

    const url = `${API_BASE_URL}/api/v1/debate/stream`;
    const token = localStorage.getItem('access_token');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          question: params.question,
          depth: params.depth,
          mode: params.mode,
          context: params.context || null,
          reference_debate_id: params.reference_debate_id || null,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      setIsConnected(true);
      setIsConnecting(false);

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';
      let eventData = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            } else if (line === '' && eventType && eventData) {
              handleSSEEvent(eventType, eventData, setState, onCompleteRef, onErrorRef);
              eventType = '';
              eventData = '';
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          console.error('Debate stream reading error:', e);
          setState(prev => ({ ...prev, status: 'error', error: 'Connection lost' }));
        }
      } finally {
        setIsConnected(false);
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        const errorMsg = (e as Error).message || 'Failed to start debate';
        console.error('Debate stream error:', errorMsg);
        setState(prev => ({ ...prev, status: 'error', error: errorMsg }));
        onErrorRef.current?.(errorMsg);
      }
      setIsConnected(false);
      setIsConnecting(false);
    }
  }, []);

  return {
    state,
    isConnected,
    isConnecting,
    startDebate,
    disconnect,
    reset,
  };
}

// ============================================================================
// SSE Event Handler
// ============================================================================

function handleSSEEvent(
  eventType: string,
  eventData: string,
  setState: React.Dispatch<React.SetStateAction<DebateStreamState>>,
  onCompleteRef: React.MutableRefObject<((result: DebateStreamState) => void) | undefined>,
  onErrorRef: React.MutableRefObject<((error: string) => void) | undefined>,
) {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(eventData);
  } catch {
    console.warn('Failed to parse SSE data:', eventData);
    return;
  }

  switch (eventType) {
    case 'debate_init':
      setState(prev => ({
        ...prev,
        status: 'initializing',
        debateId: data.debate_id as string || '',
        depth: data.depth as string || prev.depth,
        mode: data.mode as string || prev.mode,
        maxRounds: data.debate_rounds as number || 0,
        estimatedSeconds: data.estimated_seconds as number || 0,
      }));
      break;

    case 'topic_classified':
      setState(prev => ({
        ...prev,
        topicType: data.topic_type as string || '',
        tickers: data.tickers as string[] || [],
        mode: data.mode as string || prev.mode,
      }));
      break;

    case 'data_gathering_start':
      setState(prev => ({ ...prev, status: 'gathering-data' }));
      break;

    case 'data_gathering_complete':
      setState(prev => ({
        ...prev,
        dataSources: data.sources as string[] || [],
      }));
      break;

    case 'agent_assigned':
      setState(prev => ({
        ...prev,
        agents: [
          ...prev.agents,
          {
            id: data.id as string || '',
            role: data.role as string || '',
            icon: data.icon as string || '',
          },
        ],
      }));
      break;

    case 'debate_start':
      setState(prev => ({
        ...prev,
        status: 'debating',
        maxRounds: data.debate_rounds as number || prev.maxRounds,
      }));
      break;

    case 'debate_phase':
      // Phase transitions within the debate
      break;

    case 'debate_round_start':
      setState(prev => ({
        ...prev,
        currentRound: data.round_number as number || 0,
      }));
      break;

    case 'debate_agent_argument':
      setState(prev => ({
        ...prev,
        arguments: [
          ...prev.arguments,
          {
            agentId: data.agent_id as string || '',
            agentRole: data.agent_role as string || '',
            agentIcon: data.agent_icon as string || '',
            roundNumber: data.round_number as number || 0,
            argument: data.argument as string || '',
            isInitial: data.is_initial as boolean || false,
          },
        ],
      }));
      break;

    case 'mediator_check':
      setState(prev => ({
        ...prev,
        mediatorChecks: [
          ...prev.mediatorChecks,
          {
            roundNumber: data.round_number as number || 0,
            agreementScore: data.agreement_score as number || 0,
            resolved: data.resolved as string[] || [],
            unresolved: data.unresolved as string[] || [],
            facilitationNote: data.facilitation_note as string || undefined,
          },
        ],
      }));
      break;

    case 'debate_round_complete':
      setState(prev => ({
        ...prev,
        currentRound: data.round_number as number || prev.currentRound,
      }));
      break;

    case 'synthesizing':
      setState(prev => ({ ...prev, status: 'synthesizing' }));
      break;

    case 'debate_complete':
    case 'done': {
      const consensus = data.consensus as Record<string, unknown> | undefined;
      setState(prev => {
        const newState = {
          ...prev,
          status: 'done' as const,
          totalTimeSeconds: data.total_time_seconds as number || 0,
          consensus: consensus
            ? {
                synthesis: consensus.synthesis as string || '',
                score: consensus.score as number || 0,
                rawScore: consensus.raw_score as number || 0,
                resolved: consensus.resolved as string[] || [],
                unresolved: consensus.unresolved as string[] || [],
                roundsCompleted: consensus.rounds_completed as number || 0,
                initialDivergence: consensus.initial_divergence as number || 0,
                convergenceTrajectory: consensus.convergence_trajectory as number[] || [],
              }
            : prev.consensus,
        };
        // Call onComplete with final state
        if (eventType === 'done') {
          onCompleteRef.current?.(newState);
        }
        return newState;
      });
      break;
    }

    case 'error':
      setState(prev => ({
        ...prev,
        status: 'error',
        error: data.error as string || 'Unknown error',
      }));
      onErrorRef.current?.(data.error as string || 'Unknown error');
      break;

    case 'insufficient_credits':
      setState(prev => ({
        ...prev,
        status: 'error',
        error: `Insufficient credits. Required: ${data.required}, Available: ${data.available}`,
      }));
      break;

    default:
      console.log('Unknown debate event:', eventType, data);
  }
}
